import { getPermissions, hasPermission } from "../../utilis/permissionStorage";
import React, { useEffect, useState, useCallback } from "react";
import Swal from "sweetalert2";
import * as FaIcons from "react-icons/fa";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import ReactPaginate from "react-paginate";
import "../HRManagement/leavePolicies.css";
import "../Leave/leavePortal.css";

function HRManageLeaves() {
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [viewModal, setViewModal] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
  });
  const [statusCounts, setStatusCounts] = useState({
    all: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    const perms = getPermissions();
    if (perms) setPermissions(perms);
  }, []);

  const formatDate = (date) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (date) => {
    if (!date) return "—";
    const d = new Date(date);
    return `${d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} · ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  };

  const getUnitLabel = (unit) => {
    switch (unit) {
      case "full_day":
        return "Full Day";
      case "half_day":
        return "Half Day";
      case "short_leave":
        return "Short Leave";
      default:
        return unit || "—";
    }
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const isPendingRequest = (req) => (req?.status || "").toLowerCase() === "pending";

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.currentPage,
        perPage: pagination.perPage,
      };
      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      if (statusFilter !== "all") {
        params.status = statusFilter;
      }

      const response = await apiUrl.get("/hr/leaves/requests", { params });
      const body = response?.data;

      if (body?.status !== false) {
        setRequests(Array.isArray(body?.data) ? body.data : []);
        setPagination((prev) => ({
          ...prev,
          total: body?.pagination?.total ?? 0,
          lastPage: body?.pagination?.lastPage ?? 1,
          currentPage: body?.pagination?.currentPage ?? prev.currentPage,
        }));
        if (body?.status_counts) {
          setStatusCounts(body.status_counts);
        }
      } else {
        setRequests([]);
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err.response?.data?.message || "Failed to load leave requests", "error");
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, pagination.perPage, searchTerm, statusFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchRequests();
    }, searchTerm ? 400 : 0);
    return () => clearTimeout(timer);
  }, [fetchRequests, searchTerm]);

  const handlePageChange = (selectedItem) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: selectedItem.selected + 1,
    }));
  };

  const handlePerPageChange = (e) => {
    setPagination((prev) => ({
      ...prev,
      perPage: parseInt(e.target.value, 10),
      currentPage: 1,
    }));
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const approveRequest = async (id) => {
    try {
      const res = await apiUrl.post("/hr/leaves/approve", { request_id: id });
      Swal.fire("Approved!", res.data.message || "Leave approved.", "success");
      fetchRequests();
    } catch (err) {
      Swal.fire("Error", err.response?.data?.message || "Failed to approve request", "error");
    }
  };

  const rejectRequest = async (id) => {
    const { value: reason } = await Swal.fire({
      title: "Reject Leave Request",
      input: "textarea",
      inputLabel: "Reason for rejection",
      inputPlaceholder: "Enter reason...",
      showCancelButton: true,
      confirmButtonText: "Reject",
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      inputValidator: (value) => {
        if (!value?.trim()) return "Reason is required!";
      },
    });

    if (reason) {
      try {
        const res = await apiUrl.post("/hr/leaves/reject", {
          request_id: id,
          reason: reason.trim(),
        });
        Swal.fire("Rejected!", res.data.message || "Leave rejected.", "success");
        fetchRequests();
      } catch (err) {
        Swal.fire("Error", err.response?.data?.message || "Failed to reject request", "error");
      }
    }
  };

  const openViewModal = (request) => setViewModal(request);
  const closeViewModal = () => setViewModal(null);

  const getStatusChip = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "approved") {
      return <span className="lp-status-chip approved"><FaIcons.FaCheckCircle /> Approved</span>;
    }
    if (s === "rejected") {
      return <span className="lp-status-chip rejected"><FaIcons.FaTimesCircle /> Rejected</span>;
    }
    return <span className="lp-status-chip pending"><FaIcons.FaClock /> Pending</span>;
  };

  if (permissions["manage_leaves"] !== 1) {
    return (
      <div className="container py-4">
        <div className="alert alert-warning">
          You don't have permission to access this page.
        </div>
      </div>
    );
  }

  return (
    <div className="container px-4 py-4">
      <header className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          <h2 className="h3 fw-bold mb-1">Manage Leave Requests</h2>
          <p className="text-muted mb-0">Review, approve, or reject employee leave applications</p>
        </div>
        <button
          type="button"
          className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-2"
          onClick={fetchRequests}
          disabled={loading}
        >
          <FaIcons.FaSyncAlt className={loading ? "fa-spin" : ""} />
          Refresh
        </button>
      </header>

      <div className="lp-section-card lp-table-card">
        <div className="lp-section-header">
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
            <div>
              <h6 className="fw-semibold mb-0">All Requests</h6>
              <p className="text-muted small mb-0">
                {pagination.total} {statusFilter !== "all" ? statusFilter : "total"} request{pagination.total !== 1 ? "s" : ""}
                {statusCounts.pending > 0 && statusFilter === "all" && (
                  <span className="text-warning ms-1">· {statusCounts.pending} pending</span>
                )}
              </p>
            </div>
            <div className="lp-filter-tabs">
              {[
                { id: "all", label: "All" },
                { id: "pending", label: "Pending" },
                { id: "approved", label: "Approved" },
                { id: "rejected", label: "Rejected" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={statusFilter === tab.id ? "active" : ""}
                  onClick={() => handleStatusFilter(tab.id)}
                >
                  {tab.label} ({statusCounts[tab.id] ?? 0})
                </button>
              ))}
            </div>
          </div>
          <div className="input-group" style={{ maxWidth: 320 }}>
            <span className="input-group-text bg-white border-end-0">
              <FaIcons.FaSearch className="text-muted" />
            </span>
            <input
              type="text"
              className="form-control border-start-0 ps-0"
              placeholder="Name or user ID"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPagination((prev) => ({ ...prev, currentPage: 1 }));
              }}
            />
            {searchTerm && (
              <button className="btn btn-outline-secondary" onClick={() => setSearchTerm("")}>
                Clear
              </button>
            )}
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead>
              <tr>
                <th><FaIcons.FaUser className="me-2 text-muted" />Employee</th>
                <th><FaIcons.FaTags className="me-2 text-muted" />Leave Type</th>
                <th><FaIcons.FaCalendar className="me-2 text-muted" />Leave Period</th>
                <th>Duration</th>
                <th>Days</th>
                <th>Status</th>
                <th>Applied On</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i} className="placeholder-glow">
                    <td><span className="placeholder col-8" /></td>
                    <td><span className="placeholder col-6" /></td>
                    <td><span className="placeholder col-7" /></td>
                    <td><span className="placeholder col-4" /></td>
                    <td><span className="placeholder col-3" /></td>
                    <td><span className="placeholder col-5" /></td>
                    <td><span className="placeholder col-6" /></td>
                    <td><span className="placeholder col-6" /></td>
                  </tr>
                ))
              ) : requests.length === 0 ? (
                <tr>
                  <td colSpan="8">
                    <div className="lp-empty-state py-4">
                      <div className="empty-icon"><FaIcons.FaCalendarAlt size={24} /></div>
                      <h6 className="fw-semibold">No leave requests found</h6>
                      <p className="text-muted small mb-0">
                        {statusFilter === "pending"
                          ? "No pending requests right now."
                          : "Try changing filters or search."}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                requests.map((req) => {
                  const isPending = (req?.status || "").toLowerCase() === "pending";
                  return (
                    <tr key={req?.id} className={isPending ? "lp-hr-pending-row" : ""}>
                      <td>
                        <div className="fw-semibold">{req?.user_name || "—"}</div>
                        <small className="text-muted">#{req?.id}</small>
                      </td>
                      <td>
                        <div>{req?.leave_type_name || "—"}</div>
                        {req?.leave_type_code && (
                          <small className="text-muted">{req.leave_type_code}</small>
                        )}
                      </td>
                      <td>
                        <div className="lp-hr-period">
                          <span className="lp-hr-period-label">From</span>
                          <span className="fw-medium">{formatDate(req?.start_date)}</span>
                          <span className="lp-hr-period-label mt-1">To</span>
                          <span className="fw-medium">{formatDate(req?.end_date)}</span>
                        </div>
                      </td>
                      <td>
                        <span className="badge lp-badge-soft-secondary">
                          {getUnitLabel(req?.unit)}
                        </span>
                      </td>
                      <td>
                        <span className="fw-semibold">{req?.deducted_days ?? "—"}</span>
                      </td>
                      <td>{getStatusChip(req?.status)}</td>
                      <td>
                        <small className="text-muted">{formatDateTime(req?.created_at)}</small>
                      </td>
                      <td className="text-end">
                        <div className="d-flex justify-content-end gap-1 flex-wrap">
                          <button type="button" className="lp-hr-action-btn view" onClick={() => openViewModal(req)}>
                            <FaIcons.FaEye /> View
                          </button>
                          {isPending && (
                            <>
                              <button type="button" className="lp-hr-action-btn approve" onClick={() => approveRequest(req.id)}>
                                <FaIcons.FaCheck /> Approve
                              </button>
                              <button type="button" className="lp-hr-action-btn reject" onClick={() => rejectRequest(req.id)}>
                                <FaIcons.FaTimes /> Reject
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="row align-items-center p-3 border-top">
          <div className="col-auto">
            <select
              value={pagination?.perPage}
              onChange={handlePerPageChange}
              className="form-select form-select-sm"
              style={{ minWidth: 80 }}
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
          <div className="col-auto text-muted small">
            Page {pagination.currentPage} of {pagination.lastPage} · {pagination.total} entries
          </div>
          <div className="col">
            <ReactPaginate
              previousLabel="Previous"
              nextLabel="Next"
              breakLabel="..."
              pageCount={pagination.lastPage}
              marginPagesDisplayed={2}
              pageRangeDisplayed={3}
              onPageChange={handlePageChange}
              forcePage={pagination.currentPage - 1}
              containerClassName="pagination justify-content-end mb-0 pagination-sm"
              pageClassName="page-item"
              pageLinkClassName="page-link"
              previousClassName="page-item"
              previousLinkClassName="page-link"
              nextClassName="page-item"
              nextLinkClassName="page-link"
              activeClassName="active"
              disabledClassName="disabled"
            />
          </div>
        </div>
      </div>

      {viewModal && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{ display: "block", backgroundColor: "rgba(15, 23, 42, 0.5)", zIndex: 1040 }}
            onClick={closeViewModal}
          />
          <div className="modal fade show d-block" tabIndex={-1} style={{ zIndex: 1050 }}>
            <div className="modal-dialog modal-dialog-centered modal-lg" style={{ maxWidth: 720 }}>
              <div className={`modal-content border-0 rounded-4 shadow-lg overflow-hidden lp-hr-detail-modal ${isPendingRequest(viewModal) ? "is-pending" : ""}`}>
                <div className="lp-hr-detail-header">
                  <div className="d-flex align-items-start gap-3">
                    <div className="lp-hr-detail-avatar">{getInitials(viewModal?.user_name)}</div>
                    <div className="flex-grow-1 min-w-0">
                      <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                        <h5 className="modal-title fw-bold mb-0">{viewModal?.user_name || "Employee"}</h5>
                        {getStatusChip(viewModal?.status)}
                      </div>
                      <p className="lp-hr-detail-subtitle mb-0">
                        Request #{viewModal?.id} · Applied {formatDateTime(viewModal?.created_at)}
                      </p>
                    </div>
                    <button type="button" className="btn-close btn-close-white" onClick={closeViewModal} />
                  </div>
                </div>

                <div className="modal-body p-4">
                  <div className="lp-hr-detail-summary">
                    <div className="lp-hr-detail-stat">
                      <span className="lp-hr-detail-stat-label">Leave Type</span>
                      <span className="lp-hr-detail-stat-value">{viewModal?.leave_type_name || "—"}</span>
                      {viewModal?.leave_type_code && (
                        <span className="lp-balance-code mt-1">{viewModal.leave_type_code}</span>
                      )}
                    </div>
                    <div className="lp-hr-detail-stat">
                      <span className="lp-hr-detail-stat-label">Duration</span>
                      <span className="lp-hr-detail-stat-value">{getUnitLabel(viewModal?.unit)}</span>
                    </div>
                    <div className="lp-hr-detail-stat">
                      <span className="lp-hr-detail-stat-label">Days Deducted</span>
                      <span className="lp-hr-detail-stat-value accent">{viewModal?.deducted_days ?? "—"}</span>
                    </div>
                  </div>

                  <div className="lp-hr-detail-period-card">
                    <div className="lp-hr-detail-period-title">
                      <FaIcons.FaCalendarAlt className="me-2" />
                      Leave Period
                    </div>
                    <div className="lp-hr-detail-period-grid">
                      <div className="lp-hr-detail-period-block">
                        <span className="lp-hr-period-label">From</span>
                        <span className="lp-hr-detail-period-date">{formatDate(viewModal?.start_date)}</span>
                      </div>
                      <div className="lp-hr-detail-period-divider">
                        <FaIcons.FaLongArrowAltRight />
                      </div>
                      <div className="lp-hr-detail-period-block">
                        <span className="lp-hr-period-label">To</span>
                        <span className="lp-hr-detail-period-date">{formatDate(viewModal?.end_date)}</span>
                      </div>
                    </div>
                    {(viewModal?.start_time || viewModal?.end_time) && (
                      <div className="lp-hr-detail-time-row">
                        <FaIcons.FaClock className="me-2 text-muted" />
                        <span>
                          {viewModal?.start_time?.substring(0, 5) || "—"}
                          {" – "}
                          {viewModal?.end_time?.substring(0, 5) || "—"}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="lp-hr-detail-section">
                    <div className="lp-hr-detail-section-title">
                      <FaIcons.FaCommentDots />
                      Reason for Leave
                    </div>
                    <div className="lp-hr-detail-reason">
                      {viewModal?.reason?.trim() || "No reason provided"}
                    </div>
                  </div>

                  {viewModal?.admin_notes && (
                    <div className="lp-hr-detail-section lp-hr-detail-notes">
                      <div className="lp-hr-detail-section-title">
                        <FaIcons.FaStickyNote />
                        HR Notes
                      </div>
                      <div className="lp-hr-detail-reason">{viewModal.admin_notes}</div>
                    </div>
                  )}

                  <div className="lp-hr-detail-section mb-0">
                    <div className="lp-hr-detail-section-title">
                      <FaIcons.FaPaperclip />
                      Supporting Document
                    </div>
                    {viewModal?.document_url ? (
                      <button
                        type="button"
                        onClick={() => window.open(viewModal.document_url, "_blank")}
                        className="lp-hr-detail-doc-btn"
                      >
                        <FaIcons.FaFileAlt />
                        <span>View attached document</span>
                        <FaIcons.FaExternalLinkAlt className="ms-auto opacity-50" />
                      </button>
                    ) : (
                      <div className="lp-hr-detail-no-doc">No document attached</div>
                    )}
                  </div>
                </div>

                <div className="lp-hr-detail-footer">
                  <button type="button" className="btn btn-light" onClick={closeViewModal}>
                    Close
                  </button>
                  {isPendingRequest(viewModal) && (
                    <div className="d-flex gap-2 ms-auto">
                      <button
                        type="button"
                        className="btn btn-outline-danger d-inline-flex align-items-center gap-2"
                        onClick={() => {
                          closeViewModal();
                          rejectRequest(viewModal.id);
                        }}
                      >
                        <FaIcons.FaTimes />
                        Reject
                      </button>
                      <button
                        type="button"
                        className="btn btn-success d-inline-flex align-items-center gap-2"
                        onClick={() => {
                          closeViewModal();
                          approveRequest(viewModal.id);
                        }}
                      >
                        <FaIcons.FaCheck />
                        Approve Leave
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default HRManageLeaves;
